import { useMutation } from "@tanstack/react-query";
import { type AuthClient } from "~/api/AuthClient";
import { customerMutations } from "~/api/customer/mutations";
import { useAuth } from "~/hooks/useAuth";
import { hasPermission } from "~/hooks/useHasPermission";
import { type AppPermission } from "~/utils/permissions";
import { timeout } from "~/utils/promise-timeout";

export const useCancelSubscription = () => {
  return useMutation(customerMutations.cancelSubscription());
};

export const useReactivateSubscription = () => {
  return useMutation(customerMutations.reactivateSubscription());
};

export const useCreatePaymentMethodSession = () =>
  useMutation(customerMutations.createPaymentMethodSession());

export const useConfirmPaymentMethod = () =>
  useMutation(customerMutations.confirmPaymentMethod());

export const useUpgradeSubscription = (
  expectedAppPermission: AppPermission | null | undefined,
) => {
  const { authClient } = useAuth();

  return useMutation(
    customerMutations.upgradeSubscription(() =>
      tryRefreshTokenUntilPermission(authClient, expectedAppPermission),
    ),
  );
};

export const useAwaitPermission = () => {
  const { authClient } = useAuth();

  return (permission: AppPermission) =>
    tryRefreshTokenUntilPermission(authClient, permission);
};

/**
 * Due to Stripe's current async implementation, we refresh the token until we see the
 * expected permission reflected.
 * Retry-delay & max-attempts can be set/overridden via the settings object. Default: 10 tries with 500ms delay.
 */
export const tryRefreshTokenUntilPermission = async (
  authClient: AuthClient,
  permission: AppPermission | null | undefined,
  settings?: {
    retryTimeoutMs?: number;
    maxAttempts?: number;
  },
) => {
  const refreshAttemptTimeoutMs = settings?.retryTimeoutMs ?? 500;
  const maxRefreshAttempts = settings?.maxAttempts ?? 10;

  if (!permission) return true;

  for (let attempt = 1; attempt <= maxRefreshAttempts; attempt++) {
    await authClient.refreshToken();

    if (hasPermission(authClient.user, permission)) {
      return true;
    }

    if (attempt < maxRefreshAttempts) {
      await timeout(refreshAttemptTimeoutMs);
    }
  }

  throw new Error(
    `User never received permission "${permission}" within the assigned attempts (${maxRefreshAttempts}).`,
  );
};
